'use client';

import { Box, Card, Flex, LinkNext, Stack, Text } from '@instamenu/ui-system-next';
import { formatCurrency } from '@instamenu/formatters';
import { Item, RestaurantJSON, useCheckoutContext } from '@instamenu/context-checkout';
import { imgStyles, loyaltyBadge } from './menu-item.css';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import Image from 'next/image';
import { ItemCount } from '../item-count/item-count';
import { ItemStockStatus } from './item-stock-status';
import { observer } from 'mobx-react-lite';
import { makePath } from '../utils';

export type MenuItemProps = {
  item: Item;
  table: string;
  restaurant: RestaurantJSON;
  menuId?: string;
};

export const MenuItem = observer(({ item, table, restaurant, menuId }: MenuItemProps) => {
  const checkout = useCheckoutContext();
  const earningGroupIds = checkout?.loyalty?.earningItemGroupIds ?? [];
  const earnsPoints =
    checkout?.loyalty?.program?.active &&
    !!checkout?.loyalty?.status &&
    (earningGroupIds.length === 0 || earningGroupIds.includes(item.itemGroup?.id ?? ''));

  const variants = item.modifierGroups?.filter(({ isVariant }) => isVariant);
  const minMaxPrice = variants
    ?.flatMap(({ modifiers }) => modifiers)
    .reduce(
      ({ minPrice, maxPrice }, modifier) => ({
        minPrice: Math.min(minPrice, modifier.price || 0),
        maxPrice: Math.max(maxPrice, modifier.price || 0),
      }),
      { minPrice: Infinity, maxPrice: -Infinity },
    );

  const displayPrice = item.outOfStock
    ? 'Out of stock'
    : minMaxPrice && minMaxPrice.minPrice != Infinity
      ? formatCurrency(minMaxPrice.minPrice as number) +
        ' - ' +
        formatCurrency(minMaxPrice.maxPrice as number)
      : formatCurrency(item.price as number);

  const disabled = item.outOfStock;

  return (
    <LinkNext
      href={makePath({ restaurantId: restaurant.id, tableCode: table, itemId: item.id, menuId })}
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-id="menu-item"
      scroll={false}
    >
      <Card
        css={{
          padding: themeVars.pad[12],
          width: '100%',
          height: 130,
          position: 'relative',
        }}
        disabled={disabled}
        data-item-id={item.id}
      >
        <ItemCount id={item.id} />
        <Flex style={{ gap: themeVars.pad[16], height: '100%' }}>
          <Stack style={{ gap: 4, flex: 1, justifyContent: 'space-between' }}>
            <Stack style={{ gap: 4 }}>
              <Text
                variant="h3"
                maxLines={1}
                wordBreak
                css={{
                  color: disabled ? palette.gray500 : themeVars.color.brand,
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                {item.name}
              </Text>
              <ItemStockStatus item={item} />
              <Text
                variant="caption2"
                wordBreak
                css={{ color: palette.gray500, fontWeight: 400 }}
                maxLines={2}
              >
                {item.description}
              </Text>
            </Stack>
            <Flex style={{ alignItems: 'center', gap: 6 }}>
              <Text variant="numeric" css={{ color: disabled ? palette.gray400 : palette.gray700 }}>
                {displayPrice}
              </Text>
              {earnsPoints && !disabled && <span className={loyaltyBadge}>★</span>}
            </Flex>
          </Stack>
          {item.imageUrl && (
            <Box style={{ flexShrink: 0, width: 130, height: '100%' }}>
              <Image
                width={130}
                height={130}
                className={imgStyles}
                src={item.imageUrl}
                alt={item.name ?? ''}
              />
            </Box>
          )}
        </Flex>
      </Card>
    </LinkNext>
  );
});
